import type { PaginatedResult } from './paginated-result.ts'
import type { PagingOptions } from './paging-options.ts'

type SortByOptions<T> = ((a: T, b: T) => number) | keyof T

/**
 * Helper function to sort items
 */
function sortItems<T>(
  items: T[],
  sortBy: SortByOptions<T>,
  sortDirection: 'asc' | 'desc'
): T[] {
  // Determine sort function
  const sortFn =
    typeof sortBy === 'function'
      ? sortBy
      : (a: T, b: T) => {
          const aValue = sortDirection === 'asc' ? a[sortBy] : b[sortBy]
          const bValue = sortDirection === 'asc' ? b[sortBy] : a[sortBy]

          // Handle string comparison
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue)
          }

          // Handle number or date comparison
          if (
            (typeof aValue === 'number' && typeof bValue === 'number') ||
            (aValue instanceof Date && bValue instanceof Date)
          ) {
            if (aValue < bValue) {
              return -1
            }
            if (aValue > bValue) {
              return 1
            }
            return 0
          }

          // Fall back to string comparison for other types
          return String(aValue).localeCompare(String(bValue))
        }

  // Return sorted items
  return [...items].sort(sortFn)
}

/**
 * Helper function that performs pagination and returns paginated results with metadata.
 */
function paginateData<T>(
  items: T[],
  options?: PagingOptions<T>,
  normalize = false
): PaginatedResult<T> {
  if (!items || items.length === 0) {
    return {
      items: [],
      totalItems: 0,
      totalPages: 0,
      currentPage: options?.currentPage ?? 1,
      pageSize: options?.pageSize ?? 10,
      hasNextPage: false,
      hasPreviousPage: false,
    }
  }

  const sortedItems = options?.sortBy
    ? sortItems(
        items,
        options.sortBy as SortByOptions<T>,
        options.sortDirection ?? 'asc'
      )
    : [...items]

  if (!(options && (options.pageSize || options.currentPage))) {
    return {
      items: sortedItems,
      totalItems: sortedItems.length,
      totalPages: 1,
      currentPage: 1,
      pageSize: sortedItems.length,
      hasNextPage: false,
      hasPreviousPage: false,
    }
  }

  const pageSize = options.pageSize ?? 10
  const pageNumber = options.currentPage ?? 1

  if (pageSize <= 0) {
    throw new Error('Page size must be greater than 0')
  }
  if (pageNumber <= 0) {
    throw new Error('Page number must be greater than 0')
  }

  const totalItems = sortedItems.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const currentPage = normalize
    ? Math.min(pageNumber, totalPages || 1)
    : pageNumber

  const startIndex = (currentPage - 1) * pageSize
  const endIndex = Math.min(startIndex + pageSize, totalItems)

  return {
    items: sortedItems.slice(startIndex, endIndex),
    totalItems,
    totalPages,
    currentPage,
    pageSize,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  }
}

/**
 * Sorts and paginates an array of objects
 * @param items The array of objects to paginate
 * @param options Optional configuration for sorting and pagination
 * @returns The sorted items for the requested page
 */
export function dataPager<T>(items: T[], options?: PagingOptions<T>): T[] {
  return paginateData(items, options, false).items
}

/**
 * Enhanced version that returns paginated results with metadata
 * @param items The array of objects to paginate
 * @param options Optional configuration for sorting and pagination
 * @returns Paginated result with metadata
 */
export function enhancedDataPager<T>(
  items: T[],
  options?: PagingOptions<T>
): PaginatedResult<T> {
  return paginateData(items, options, true)
}
